/**
 * Map editor rooms (SC-422 plan D2): a platform admin opens a map's editor room from the Maps
 * view. The server finds or starts it; MainMenu then joins it through its usual permissions and
 * join flow, which it picks up from `MAP_EDITOR_JOIN_EVENT`.
 */
import { getServerBaseUrl } from '../utils/serverUrl'
import { getStoredSessionToken } from '../utils/sessionToken'

export const MAP_EDITOR_JOIN_EVENT = 'sevv:join-map-editor-room'

export interface MapEditorRoomTarget {
  mapId: string
  roomId: string
  customRoomId: string
}

export async function openMapEditorRoom(mapId: string): Promise<MapEditorRoomTarget> {
  const response = await fetch(`${getServerBaseUrl()}/api/admin/map-editor-rooms/${encodeURIComponent(mapId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getStoredSessionToken()}` },
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok || !body.success) {
    throw new Error(body.error || `Could not open the map editor room (${response.status})`)
  }
  return { mapId, roomId: body.roomId, customRoomId: body.customRoomId }
}

const RETURN_MAP_KEY = 'sevv:map-editor-return-map'
const RETURN_MAP_TTL_MS = 60_000

/**
 * Leaving a room reloads the page, and admin URLs are refused while permissions load after a
 * reload, so `/admin/maps` can't carry the map back. The map goes through sessionStorage instead:
 * MainMenu opens Admin → Maps once the admin check passes, and AdminMaps selects the map and
 * clears it. It stays until then, so a MainMenu remount during start-up still finds it.
 */
export function rememberMapEditorReturn(mapId: string): void {
  try {
    sessionStorage.setItem(RETURN_MAP_KEY, JSON.stringify({ mapId, at: Date.now() }))
  } catch {
    // Storage blocked: the admin lands on the dashboard as before.
  }
}

/** The map to return to, unless the hint is older than one reload could take (never consumed). */
export function peekMapEditorReturn(): string | null {
  try {
    const raw = sessionStorage.getItem(RETURN_MAP_KEY)
    if (!raw) return null
    const { mapId, at } = JSON.parse(raw)
    return typeof mapId === 'string' && Date.now() - at < RETURN_MAP_TTL_MS ? mapId : null
  } catch {
    return null
  }
}

export function clearMapEditorReturn(): void {
  try {
    sessionStorage.removeItem(RETURN_MAP_KEY)
  } catch {
    // Nothing stored.
  }
}

/** Opens the map's editor room and asks MainMenu to join it. */
export async function joinMapEditorRoom(mapId: string): Promise<MapEditorRoomTarget> {
  const target = await openMapEditorRoom(mapId)
  window.dispatchEvent(new CustomEvent<MapEditorRoomTarget>(MAP_EDITOR_JOIN_EVENT, { detail: target }))
  return target
}
